/**
 * QRCodeGenerator
 *
 * Install: npm install qrcode
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { partial } from '../../util/translation/translation';

const s = partial('shared');

export function QRCodeGenerator({ defaultUrl = '' }) {
  const [url, setUrl] = useState(defaultUrl);
  const [ec, setEc] = useState('M');
  const [size, setSize] = useState(200);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const canvasRef = useRef(null);
  const hasQR = useRef(false);

  const generate = useCallback(
    async (value) => {
      setError(null);

      if (!value.trim()) return;

      try {
        new URL(value);
      } catch {
        setError('Enter a valid URL including https://');
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) return;

      try {
        await QRCode.toCanvas(canvas, value, {
          width: size,
          margin: 2,
          errorCorrectionLevel: ec,
          color: { dark: '#000000', light: '#ffffff' },
        });
        hasQR.current = true;
      } catch (err) {
        setError(err.message);
      }
    },
    [ec, size]
  );

  useEffect(() => {
    if (url) generate(url);
  }, [ec, size]);

  function handleDownload() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'qrcode.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  function handleCopy() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        setError('Copy not supported in this browser.');
      }
    });
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        maxWidth: 480,
      }}
    >
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="url"
          value={url}
          placeholder="https://example.com"
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && generate(url)}
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid #ddd',
            fontSize: 14,
          }}
        />
        <button
          onClick={() => generate(url)}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            background: '#111',
            color: '#fff',
            border: 'none',
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          {s('create')}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 16 }}>
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            fontSize: 13,
            color: '#666',
          }}
        >
          Error correction
          <select
            value={ec}
            onChange={(e) => setEc(e.target.value)}
            style={{
              padding: '4px 8px',
              borderRadius: 6,
              border: '1px solid #ddd',
              fontSize: 13,
            }}
          >
            <option value="L">Low (L)</option>
            <option value="M">Medium (M)</option>
            <option value="Q">Quartile (Q)</option>
            <option value="H">High (H)</option>
          </select>
        </label>

        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            fontSize: 13,
            color: '#666',
          }}
        >
          Size
          <select
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
            style={{
              padding: '4px 8px',
              borderRadius: 6,
              border: '1px solid #ddd',
              fontSize: 13,
            }}
          >
            <option value={160}>Small (160px)</option>
            <option value={200}>Medium (200px)</option>
            <option value={260}>Large (260px)</option>
          </select>
        </label>
      </div>

      <div
        style={{
          border: '1px solid #eee',
          borderRadius: 10,
          padding: 16,
          display: 'inline-flex',
          background: '#fff',
        }}
      >
        <canvas ref={canvasRef} width={size} height={size} />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handleDownload}
          disabled={!hasQR.current}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: '1px solid #ddd',
            fontSize: 13,
            cursor: 'pointer',
            textTransform: 'capitalize',
          }}
        >
          {s('download')} PNG
        </button>
        <button
          onClick={handleCopy}
          disabled={!hasQR.current}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: '1px solid #ddd',
            fontSize: 13,
            cursor: 'pointer',
            textTransform: 'capitalize',
          }}
        >
          {copied ? 'Copied!' : `${s('copy')} ${s('image')}`}
        </button>
      </div>

      {error && <p style={{ fontSize: 13, color: '#c00' }}>{error}</p>}
    </div>
  );
}
